import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, publishReplay, refCount } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserCardioWorkout } from '../models/user-cardio-workout.model';
import { DateHelperService } from './date-helper.service';

@Injectable({
  providedIn: 'root',
})
export class UserCardioService {
  error = new Subject<string>();
  slug = '/user_cardio_workouts';
  resourceName = 'user_cardio_workouts';
  level = 1;
  allWorkouts?: Observable<UserCardioWorkout[]> | null = null;
  allExercises?: any;
  levelHistory?: any;

  levelVideos: any = {
    1: [{ title: 'Prep Fit Level 1', video: '6448e99bf09b6.mp4' }, { title: 'Warm up', video: '6448eae05ed88.mp4' }, { title: 'Cool Down', video: '6448ec927ad44.mp4' }],
    2: [{ title: 'Prep Fit Level 2', video: '6448e9b1e451d.mp4' }, { title: 'Warm up', video: '6448eae05ed88.mp4' }, { title: 'Cool Down', video: '6448ec927ad44.mp4' }],
    3: [{ title: 'Prep Fit Level 3', video: '6448e9cd2cb43.mp4' }, { title: 'Warm up', video: '6448eae05ed88.mp4' }, { title: 'Cool Down', video: '6448ec927ad44.mp4' }, { title: 'HIIT Session', video: '6448ecb7813c8.mp4' }],
  };

  constructor(
    private http: HttpClient,
    private dateHelperService: DateHelperService
  ) {}

  setLevel(level: number) {
    this.level = level;
  }
  fetchAll(): Observable<UserCardioWorkout[]> {
    if (!this.allWorkouts) {
      this.allWorkouts = this.http
        .get<any>(environment.apiUrl + this.slug, {
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            const returnArray: UserCardioWorkout[] = [];
            responseData['_embedded'][this.resourceName].forEach(
              (item: any) => {
                returnArray.push(item);
              }
            );
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.allWorkouts;
  }

  clearCache() {
    this.allWorkouts = null;
    this.allExercises = null;
    this.levelHistory = null;
  }

  create(
    minutes: number,
    workout_date: string,
    exercise_id: number,
    intensity: number
  ) {
    this.clearCache();
    const payload = {
      minutes,
      workout_date,
      exercise_id,
      intensity,
    };
    return this.http.post<UserCardioWorkout>(
      environment.apiUrl + this.slug,
      payload,
      {
        observe: 'response',
      }
    );
  }

  getDaySteps(date: string, data: any): number {
    let daySteps = 0;
    data.forEach((item: UserCardioWorkout) => {
      if (item.workout_date === date) {
        if (item.minutes) {
          daySteps = +item.minutes;
        }
      }
    });
    return daySteps;
  }

  getTotalSteps(data: any) {
    let totalSteps = 0;
    data.forEach((day: any) => {
      totalSteps += +day.minutes;
    });
    return totalSteps;
  }
  getBestDay(data: any) {
    let topSteps = -1;
    let bestDay;
    data.forEach((day: any) => {
      if (+day.minutes > topSteps) {
        bestDay = day;
        topSteps = +day.minutes;
      }
    });
    return bestDay;
  }
  generateWeekArray(currentWeek: any, data: any) {
    const weekArray: any = [];
    currentWeek.forEach((day: any) => {
      let currentDay = {
        date: this.dateHelperService.formatDate(day, 'YYYY-MM-DD'),
        minutes: 0,
        moderateMinutes: 0,
        vigorousMinutes: 0,
      };
      data.forEach((item: UserCardioWorkout) => {
        if (item.workout_date === currentDay.date) {
          if (item.minutes) {
            currentDay.minutes += +item.minutes;

            if (item.intensity && +item.intensity >= 4 && +item.intensity < 7) {
              currentDay.moderateMinutes += +item.minutes;
            }
            if (item.intensity && +item.intensity >= 7) {
              currentDay.vigorousMinutes += +item.minutes;
            }
          }
        }
      });
      weekArray.push(currentDay);
    });

    return weekArray;
  }

  generatePastSevenDaysArray(data: any) {
    const weekArray: any = [];
    for (let i = 0; i < 7; i++) {
      let thisDay = moment().subtract(i, 'days').format('YYYY-MM-DD');

      let currentDay = {
        date: thisDay,
        minutes: 0,
        low: 0,
        mod: 0,
        high: 0,
      };
      data.forEach((item: UserCardioWorkout) => {
        if (item.workout_date === currentDay.date) {
          if (item.minutes) {
            currentDay.minutes += +item.minutes;
            // do the intensities
            if (item.intensity) {
              if (+item.intensity < 4) {
                currentDay.low += +item.minutes;
              }
              if (+item.intensity > 3 && +item.intensity < 7) {
                currentDay.mod += +item.minutes;
              }
              if (+item.intensity > 6) {
                currentDay.high += +item.minutes;
              }
            }
          }
        }
      });
      weekArray.push(currentDay);
    }

    return weekArray;
  }

  generateWeekData(week: any, data: any) {
    const weekData: any = [];
    week.forEach((day: any) => {
      let thisDay = moment(day).format('YYYY-MM-DD');

      let currentDay = {
        date: thisDay,
        minutes: 0,
        low: 0,
        mod: 0,
        high: 0,
      };
      data.forEach((item: UserCardioWorkout) => {
        if (item.workout_date === currentDay.date) {
          weekData.push(item);
        }
      });
    });
    return weekData;
  }

  fetchExercises(): Observable<any[]> {
    if (!this.allExercises) {
      this.allExercises = this.http
        .get<any>(environment.apiUrl + '/exercises', {
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            return responseData['_embedded']['exercises'];
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.allExercises;
  }

  updateLevel(level: number) {
    this.clearCache();
    const payload = {
      level: level,
      area: 'fit',
    };
    return this.http.post<any>(
      environment.apiUrl + '/user_move_well_levels',
      payload,
      {
        observe: 'response',
      }
    );
  }

  fetchLevelHistory(): Observable<any[]> {
    if (!this.levelHistory) {
      let searchParams = new HttpParams();

      searchParams = searchParams.append('area', 'fit');
      this.levelHistory = this.http
        .get<any>(environment.apiUrl + '/user_move_well_levels', {
          responseType: 'json',
          params: searchParams,
        })
        .pipe(
          map((responseData) => {
            const returnArray: any[] = [];
            responseData['_embedded']['user_move_well_levels'].forEach(
              (item: any) => {
                returnArray.push(item);
              }
            );
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.levelHistory;
  }

  fetch(id: number) {
    return this.http
      .get<any>(environment.apiUrl + this.slug + '/' + id, {
        responseType: 'json',
      })
      .pipe(
        map((responseData) => {
          const item = new UserCardioWorkout(
            +responseData.user_cardio_workout_id,
            +responseData.user_id,
            responseData.workout_date,
            responseData.level,
            responseData.exercise_id,
            responseData.exercise_other,
            responseData.minutes,
            responseData.seconds,
            responseData.intensity,
            responseData.difficulty,
            responseData.created,
            responseData.modified
          );
          return item;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  update(
    id: number,
    minutes: number,
    workout_date: string,
    exercise_id: number,
    intensity: number
  ) {
    this.clearCache();
    const payload = {
      workout_date: moment(workout_date).format('YYYY-MM-DD'),
      minutes,
      exercise_id,
      intensity,
    };
    return this.http.patch<UserCardioWorkout>(
      environment.apiUrl + this.slug + '/' + id,
      payload,
      {
        observe: 'response',
      }
    );
  }

  delete(id: number) {
    this.clearCache();
    return this.http.delete<{ name: string }>(
      environment.apiUrl + this.slug + '/' + id
    );
  }

  fetchSyncedActivities(): Observable<any[]> {
    return  this.http
      .get<any>(environment.apiUrl + '/user_synced_activities', {
        responseType: 'json',
      })
      .pipe(
        map((responseData) => {
          const returnArray: any[] = [];
          responseData['_embedded']['user_synced_activities'].forEach((item: any) => {
            returnArray.push(item);
          });
          return returnArray;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  setSyncedActivityStatus(id:any, status:any){
    const payload = {
     status
    };
    return this.http.patch<any>(
      environment.apiUrl + '/user_synced_activities/' + id,
      payload,
      {
        observe: 'response',
      }
    );
  }
}
